<template>
  <div style="height: 100%" :id="'tableparent-' + tableID">
    <teleport :to="getContainer()" :disabled="fullscreenTable == false">
      <div
        style="height: 100%"
        class="custom-table"
        :class="{ fullscreen: fullscreenTable == true }"
        ref="reftableDiv"
        :id="tableID"
        v-loading="spinning"
      >
        <SchemaForm
          v-if="searchBox"
          ref="queryFormRef"
          class="bg-white dark:bg-black mb-16px pt-24px pr-24px table-search-form"
          submit-on-reset
          v-bind="getFormProps"
          :is-outline="false"
          :table-instance="tableAction"
          @toggle-advanced="(e) => $emit('toggle-advanced', e)"
          @submit="handleSubmit"
        >
          <template
            v-for="item in getFormSlotKeys"
            #[replaceFormSlotKey(item)]="data"
          >
            <slot :name="item" v-bind="data || {}"></slot>
          </template>
        </SchemaForm>
        <div class="bg-white dark:bg-black">
          <ToolBar
            v-if="showToolBar"
            :title="headerTitle"
            :title-tooltip="titleTooltip"
            :show-table-setting="showTableSetting"
            :showOrderParama="showOrderParama"
            :showFullScreen="fullscreenTable"
            :showAutoForm="props.queryFilterForm"
            :export-file-name="exportFileName"
            :is-export-current="isExportCurrent"
            class="table-tool-bar"
            @cols-change="handleColsChange"
            @order-change="handleOrderChange"
            @full-screen="handleFullScreen"
            @complex-query="handleComplexQuery"
            style="height: 26px"
          >
            <template v-for="name of Object.keys($slots)" #[name]="data">
              <slot :name="name" v-bind="data || {}"></slot>
            </template>
          </ToolBar>
          <SchemaForm
            ref="editTableFormRef"
            no-style
            :initial-values="editFormModel"
            :show-action-button-group="false"
            :show-advanced-button="false"
            :style="{ height: showToolBar ? 'calc(100% - 26px)' : '100%' }"
          >
            <Table
              class="base-table"
              ref="tableRef"
              :id="tableID"
              v-bind="getBindValues"
              :columns="showCols"
              :data-source="tableData"
              :scroll="unref(scroll)"
              :row-class-name="
                props.rowClassName ? props.rowClassName : setRowClassName
              "
              @resize-column="handleResizeColumn"
              :customRow="localcustomRow"
              :customHeaderRow="headerClick"
              :customCell="cellClick"
              :customHeaderCell="headerCellClick"
              @change="handleTableChange"
            >
              <template
                v-for="(_, slotName) of $slots"
                #[slotName]="slotData"
                :key="slotName"
              >
                <slot :name="slotName" v-bind="slotData"></slot>
              </template>
              <template #bodyCell="slotData">
                <slot name="bodyCell" v-bind="slotData"></slot>
              </template>
            </Table>
          </SchemaForm>
        </div>
        <!-- </a-spin> -->
      </div>
      <ltAModel
        v-model:open="visible"
        @ok="onOk"
        :title="$t('component.table.showInDlg')"
        :afterClose="handleOnClose"
        :width="editDlgWidth"
      >
        <div :style="{ height: editDlgHeight, width: '100%' }">
          <SchemaForm
            ref="editFormRef"
            class="bg-white dark:bg-black mb-16px pt-24px pr-24px table-edit-row-form"
            submit-on-reset
            v-bind="getRowFormProps"
            :initial-values="currentRowData"
            :table-instance="tableAction"
            @submit="handleEditRowSubmit"
            @submit-error="handleError"
          >
            <template
              v-for="item in getRowSlotKeys"
              #[replaceRowSlotKey(item)]="data"
            >
              <slot :name="item" v-bind="data || {}"></slot>
            </template>
          </SchemaForm>
        </div>
      </ltAModel>
    </teleport>
  </div>
</template>

<script lang="tsx" setup>
import {
  useSlots,
  onMounted,
  ref,
  nextTick,
  watch,
  computed,
  onBeforeMount,
  onUnmounted,
  unref,
} from "vue";
import { message, Table } from "ant-design-vue";
import {
  useTableMethods,
  createTableContext,
  useExportData2Excel,
  useTableForm,
  useRowForm,
  useTableState,
  useEditable,
  useColumns,
} from "./hooks";
import { ToolBar } from "./components";
import { dynamicTableProps, dynamicTableEmits } from "./dynamic-table";
import { TableActionType } from "./types";
import { SchemaForm } from "../../lt-a-form/src";
import { GuidID, getBaseUrlInfo } from "./hooks/commonMethods";
import { formatAutoCols } from "./hooks/editable-cols";
import type { RowClassName } from "ant-design-vue/lib/vc-table/interface";
import * as signalR from "@microsoft/signalr";
import ltAModel from "../../lt-a-model";
import { useScroll } from "./hooks/useScroll";
import { i18n } from "../../../locales/index";
import { isBoolean } from "@/utils/is";
import type { TableColumn } from "./types";

const spinning = ref(false);
const { t } = i18n.global;

const tableID = "table-" + GuidID(); //用来识别表格实例
// console.log("生成的表格ID为：" + tableID)
const queryID = GuidID();
let fullscreenTable = ref(false); //用来控制表格是否全屏显示
const visible = ref(false);
const currentRowData = ref<Recordable>({});
const ifCaleScrollY = ref(false);
const ifDisByClient = ref(false);

let connection = ref<signalR.HubConnection | null>(null);
let currentKey = ref(-1);
let lastCurrentKey = ref(-1);
const editFormRef = ref(null);

const props = defineProps(dynamicTableProps);
const emit = defineEmits(dynamicTableEmits);
const slots = useSlots();
const currentRowKeyValue = ref("");
const tableState = useTableState({ props, slots, tableID, emit });

const {
  tableRef,
  tableData,
  queryFormRef,
  editTableFormRef,
  getBindValues,
  editFormModel,
} = tableState;
// 表格内部方法

const editableHooks = useEditable({ props, state: tableState });
const tableMethods = useTableMethods({
  state: tableState,
  props,
  emit,
  lastCurrentKey,
  currentKey,
  visible,
  currentRowData,
  spinning,
  currentRowKeyValue,
  tableID,
  editableHooks,
  connection,
  ifDisByClient,
});
const {
  setProps,
  getTableData,
  handleSubmit,
  reload,
  handleTableChange,
  handleResizeColumn,
  getOrderParama,
  orderChangeByForm,
  getDataByMonitor,
  startConect,
  setCellTtitle,
  initColumnFilter,
  getComplexCondition,
  rowClick,
  headerClick,
  cellClick,
  headerCellClick,
  editRowDlgOnOK,
  fetchData,
  queryGroupData,
} = tableMethods;

const localcustomRow = computed(() => {
  return props.customRow ? props.customRow : handleRowClick;
});

const tableAction: TableActionType = {
  setProps,
  reload,
  getTableData,
  ...editableHooks,
};

const editDlgWidth = computed(() => {
  return props.editDlgWidth ? props.editDlgWidth : "800px";
});

const editDlgHeight = computed(() => {
  return props.editDlgHeight ? props.editDlgHeight : "600px";
});

const showCols = computed(() => {
  // 合并 innerProps，使 setProps 更新的列宽对所有模式生效
  const columns = unref(tableState.getProps).columns ?? props.columns;
  //兼容外部列功能
  if (props.isUseOutCols == true) {
    return columns;
  }
  if (props.isAutoEditable == true) {
    const cols = columns;
    if (props?.showIndex) {
      const indexCols = cols.filter((item) => {
        return item.dataIndex == "INDEX";
      });
      if (indexCols.length == 0) {
        cols.unshift({
          dataIndex: "INDEX",
          title: t("component.table.index"),
          width: 60,
          align: "center",
          fixed: "left",
          Visible: true,
          Style: "INDEX",
          customRender: ({ index }) => {
            const getPagination = unref(tableState.paginationRef);
            if (isBoolean(getPagination)) {
              return index + 1;
            }
            const { current = 1, pageSize = 10 } = getPagination!;
            return ((current < 1 ? 1 : current) - 1) * pageSize + index + 1;
          },
        } as TableColumn);
      }
    }
    const tempCols = formatAutoCols(
      props,
      emit,
      tableState,
      cols,
      spinning,
      tableAction,
    );

    return tempCols;
  } else {
    return innerColumns.value;
  }
});

// 表格列的配置描述
const { innerColumns } = useColumns({
  props,
  slots,
  state: tableState,
  methods: tableMethods,
  tableAction,
  emit,
  tableID,
  spinning,
});

const showOrderParama = computed(() => {
  //联合排序功能，只用于后台排序，本地排序不做处理
  return props.OrderParam.length > 0 && props.dataSource?.length == 0;
});

const isExportCurrent = computed(() => {
  //联合排序功能，只用于后台排序，本地排序不做处理
  return typeof props.url == "undefined";
});
// 搜索表单
const { getFormProps, replaceFormSlotKey, getFormSlotKeys } = useTableForm({
  tableState,
  tableMethods,
  slots,
});

// 查看详细信息表单
const { getRowFormProps, replaceRowSlotKey, getRowSlotKeys } = useRowForm({
  tableState,
  tableMethods,
  slots,
});

// 表单导出
const exportData2ExcelHooks = useExportData2Excel({
  props,
  state: tableState,
  methods: tableMethods,
});

const { scroll } = useScroll({
  props,
  tableID,
  tableData,
  tableMethods,
  tableState,
  ifCaleScrollY,
});

// 当前组件所有的状态和方法
const instance = {
  ...props,
  ...tableState,
  ...tableMethods,
  ...editableHooks,
  ...exportData2ExcelHooks,
};

createTableContext(instance);
defineExpose(instance);

const getContainer = function () {
  return document.body;
};
//行点击事件
const handleRowClick = function (record, index) {
  return rowClick(record, index, currentKey);
};

const handleColsChange = function (cols) {
  // console.log("执行列改变事件")
  initColumnFilter(tableID, props, cols);
};

const handleOrderChange = function (value, isUnion) {
  orderChangeByForm(value, isUnion);
  const sendData = {
    value: value,
    isUnion: isUnion,
  };
  //像外部推送排序详细信息
  emit("order-change", sendData);
};

const handleFullScreen = function (value) {
  fullscreenTable.value = value;
};

const handleComplexQuery = function (value) {
  getComplexCondition(value);
};

const setRowClassName = function (record, index): string | RowClassName<any> {
  //是否高亮显示当前行
  if (props.highlightCurrentRow && index == currentKey.value) {
    return "curren-row";
  }
  if (props.stripe) {
    if (index % 2 === 1) {
      return "table-striped";
    }
  } else {
    return "";
  }
  return "";
};

watch(
  [() => props.dataSource],
  () => {
    if (props.dataSource) {
      fetchData();
    }
  },
  {
    immediate: true,
    deep: true,
  },
);

watch(
  () => props.queryParams,
  () => {
    getTableData();
  },
  {
    immediate: true,
    deep: true,
  },
);

watch(
  () => tableData.value,
  () => {
    //console.log("当前展示数据是：" + JSON.stringify(tableData.value))
  },
);

const onOk = () => {
  if (editFormRef.value) {
    //@ts-ignore
    //借用底层函数实现form表单数据
    editFormRef.value.submit();
  }
  // visible.value = false
};

const handleEditRowSubmit = (value) => {
  editRowDlgOnOK(value);
  if (!props.editDlgCloseByOut) {
    visible.value = false;
  }
};

const handleError = () => {
  message.warn(t("component.table.validWarning"));
  visible.value;
};

const handleOnClose = function () {
  visible.value = false;
};

watch(
  () => [props.setCurrentNum, showCols.value],
  (val, oldVal) => {
    if (props.setCurrentNum != -1) {
      //设置当前行关键字
      currentKey.value = props.setCurrentNum;
      //@ts-ignore
      lastCurrentKey.value = oldVal[0];
    }
  },
);

watch(
  () => lastCurrentKey.value,
  (val, oldVal) => {
    console.log("当前行关键字改变了：" + val);
  },
);

watch(
  () => props.selectTypeAfterload,
  (val, oldVal) => {
    if (props.selectTypeAfterload == 1) {
      currentKey.value = 0;
      lastCurrentKey.value = currentKey.value;
    } else if (props.selectTypeAfterload == 2) {
      currentKey.value = lastCurrentKey.value;
      lastCurrentKey.value = currentKey.value;
    }
  },
);

watch(
  () => [props.dataName, props.url],
  () => {
    //20250715为低代码增加的功能，常规业务中，意义不大
    getTableData();
  },
);

const localSingnalrUrl = computed(() => {
  if (props.signalrUrl) {
    return props.signalrUrl;
  } else {
    return getBaseUrlInfo() + "/notify";
  }
});

if (props.ifMonitor) {
  //@ts-ignore
  connection.value = new signalR.HubConnectionBuilder() //服务器地址
    .withUrl(localSingnalrUrl.value, {})
    .configureLogging(signalR.LogLevel.Information)
    .build();
}

onBeforeMount(() => {
  if (props.ifMonitor) {
    getDataByMonitor(queryID);
  }
});

onMounted(() => {
  getOrderParama();
  nextTick(() => {
    getTableData();
    if (props.ifFixed) setCellTtitle(tableID);
  });
  if (props.ifMonitor) {
    startConect();
    if (connection.value) {
      connection.value.onclose(async () => {
        if (ifDisByClient.value == false) {
          console.log("尝试重新连接");
          await startConect();
        }
      });
    }
  }
  //
  //getLocalShowCols()
});

onUnmounted(() => {
  if (props.ifMonitor) {
    if (connection.value) {
      connection.value.send("UnSubcribe", queryGroupData.ScruSubcribeId);
      connection.value.stop();
      connection.value = null;
    }
  }
});
</script>

<style lang="less" scoped>
:deep(.ant-table-wrapper) {
  padding: 0 6px 6px 6px;

  .ant-table {
    .ant-table-title {
      display: flex;
    }

    .ant-image:hover {
      cursor: zoom-in;
    }

    .ant-btn {
      margin-right: 10px;
    }
  }
}

.bg-white:deep(.ant-table-wrapper) {
  height: 100%;
}

.bg-white :deep(.ant-spin-nested-loading) {
  height: 100%;
}

.bg-white :deep(.ant-spin-container) {
  height: 100%;
}

.custom-table {
  &.fullscreen {
    top: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    max-width: 100vw !important;
    position: absolute;
    background-color: white;
  }

  .ant-modal {
    position: fixed;
    padding: 0;
    min-height: 200px;
    min-width: 200px;

    .ant-modal-close {
      top: 6px;
      right: 6px;

      &:hover,
      &:focus {
        color: rgba(0, 0, 0, 0.45);
      }

      .ant-space-item:hover .anticon,
      .ant-space-item:focus .anticon {
        color: rgba(0, 0, 0, 0.75);
        text-decoration: none;
      }

      .ant-modal-close-x {
        width: 50px;
        height: 50px;
        line-height: 44px;

        .ant-space {
          width: 100%;
          height: 100%;
        }
      }
    }

    .ant-modal-content {
      /* width: ~'v-bind("props.width")px'; */
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
      min-height: 200px;
      min-width: 200px;
      overflow: hidden;

      .ant-modal-body {
        flex: auto;
        overflow: auto;
        height: 100%;
      }
    }
  }
}

.actions > * {
  margin-right: 10px;
}

.bg-white .ltaui-form .ant-form-item > .ant-row,
.bg-white .ant-form-item > .ant-row {
  border: 0px solid #d1dbe5 !important;
}
</style>

<style scoped>
.bg-white {
  height: 100%;
}

.bg-white .ltaui-form {
  border-left: 1px solid #d1dbe5 !important;
}

.custom-table:deep(.table-striped) td {
  background-color: #fafafa !important;
}

.custom-table:deep(.curren-row) td {
  background-color: var(--currentListColor, #00b4ed) !important;
  font-size: 14px !important;
  font-weight: bold !important;
}

/* 固定列需不透明背景与更高层级，避免横向滚动时数据遮挡固定列内容 */
.custom-table:deep(.ant-table-thead .ant-table-cell-fix-left),
.custom-table:deep(.ant-table-thead .ant-table-cell-fix-right) {
  background-color: #fafafa !important;
  z-index: 3 !important;
}

.custom-table:deep(.ant-table-tbody .ant-table-cell-fix-left),
.custom-table:deep(.ant-table-tbody .ant-table-cell-fix-right) {
  background-color: #fff !important;
  z-index: 2 !important;
}

.custom-table:deep(tr.table-striped td.ant-table-cell-fix-left),
.custom-table:deep(tr.table-striped td.ant-table-cell-fix-right) {
  background-color: #fafafa !important;
}

.custom-table:deep(tr.curren-row td.ant-table-cell-fix-left),
.custom-table:deep(tr.curren-row td.ant-table-cell-fix-right) {
  background-color: var(--currentListColor, #00b4ed) !important;
}

.custom-table:deep() td {
  padding: 1px 4px !important;
  white-space: nowrap;
}

.custom-table:deep()tr,
td {
  white-space: nowrap;
  text-overflow: ellipsis;
  word-break: break-word;
  overflow: hidden;
}

.custom-table:deep(.ant-table-pagination.ant-pagination) {
  margin: 10px 0 !important;
}

.edit-dialog:deep(.ant-modal-body) {
  padding: 10px !important;
}

/* 调整查询form显示样式 */
.custom-table :deep(.ant-col) {
  height: 38px;
  padding: 2px;
}

.custom-table :deep(.ant-form-item) {
  margin-bottom: 0px;
}

.base-table :deep(.ant-col) {
  height: 24px;
  padding: 0px;
}

.base-table :deep(.ant-form-item) {
  margin-bottom: 0px;
}

.custom-table.fullscreen {
  z-index: 9999;
}

.table-tool-bar {
  border-left: 1px solid #d1dbe5;
  border-top: 1px solid #d1dbe5;
  border-right: 1px solid #d1dbe5;
  padding-top: 4px;
}
</style>

<style>
.bg-white .ltaui-form > .ant-row {
  width: 100% !important;
  height: 100%;
}

.bg-white .ltaui-form .ant-table,
.bg-white .ltaui-form .ant-table-container,
.bg-white .ltaui-form .ant-table-container {
  height: 100%;
}

.bg-white .ltaui-form .ant-table > .ant-table-container > .ant-table-content {
  overflow-y: auto !important;
}

.editable-cell .ant-form-item {
  border-left: 0px !important;
}

.ant-col-0.bg-white {
  display: none !important;
}

.ltaui-form .ant-table-tbody td:not(.ant-table-cell-fix-left):not(.ant-table-cell-fix-right) {
  background-color: inherit;
}
</style>
